import React, { useState, useEffect, memo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import SearchBar from 'components/shared/SearchBar/SearchBar';

import { ReturnButton } from 'components/shared/ReturnButton/ReturnButton';
import './RoleConfigPage.css';
import { IconLoader } from 'common/IconLoader';
import { RoleConfigTable } from 'components/features/RoleConfig/RoleConfigTable/RoleConfigTable';
import { doGetRoleList, doGetAllPermissionsList, doRoleUpdate, doRoleDelete } from 'redux/actions';
import { RoleConfigModal } from 'components/features/RoleConfig/RoleConfigModal/RoleConfigModal';
import Modal from 'components/shared/Modal/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const RoleConfigPage = (props) => {
  let userPermissions = useSelector((store) => store.data.rolePermissions.permissions);

  const EditRoleConfigMode = {
    NEW: 'NEW',
    UPDATE: 'UPDATE',
    READONLY: 'READONLY'
  };

  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [allServicePermissions, setAllServicePermissions] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const setTargetRoleId = useRef(null);
  const currRoleData = useRef(null);

  const [roleModalEditMode, setRoleModalEditMode] = useState(null);
  const [isDeleteRoleModalOpen, setIsDeleteRoleModalOpen] = useState(false);

  const [targetDeleteRole, setTargetDeleteRole] = useState({ id: null, name: null });
  const [isDeleteRolePending, setIsDeleteRolePending] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      doGetRoleList(
        (result) => {
          setRoles(result);
          setFilteredRoles(result);
        },
        (error) => {
          setErrorMessage(error);
        }
      )
    );
    dispatch(
      doGetAllPermissionsList(
        (result) => {
          setAllServicePermissions(result);
        },
        (error) => {
          setErrorMessage(error);
        }
      )
    );
  }, []);

  let deleteRoleModal = (
    <Modal
      closeTargetRef={null}
      close={() => {
        setIsDeleteRoleModalOpen(false);
      }}
      animate={false}
      animateX={false}
      opaque={true}
      height={200}
    >
      <div className="RoleConfigPage_DeleteRoleModal">
        <div className="RoleConfigPage_DeleteRoleModal_Header_Div">
          {'Delete Role '}
          <div className="RoleConfigPage_DeleteRoleModal_Header_Div_Role">{targetDeleteRole.name}</div>?
        </div>
        <div className="RoleConfigPage_DeleteRoleModal_Buttons">
          <button
            className="Global_RoundedButton RoleConfigPage_DeleteRole_CancelButton"
            disabled={isDeleteRolePending}
            onClick={() => {
              setIsDeleteRoleModalOpen(false);
            }}
          >
            Cancel
          </button>
          <button
            className="Global_RoundedButton RoleConfigPage_DeleteRole_ConfirmButton"
            disabled={isDeleteRolePending}
            onClick={() => {
              setIsDeleteRolePending(true);
              dispatch(
                doRoleDelete(
                  targetDeleteRole.id,
                  () => {
                    dispatch(
                      doGetRoleList(
                        (result) => {
                          setRoles(result);
                          setFilteredRoles(result);
                        },
                        (error) => {
                          setErrorMessage(error);
                          setIsDeleteRoleModalOpen(false);
                        }
                      )
                    );
                    setIsDeleteRoleModalOpen(false);
                    setIsDeleteRolePending(false);
                  },
                  (error) => {
                    setErrorMessage(error);
                    setIsDeleteRoleModalOpen(false);
                  }
                )
              );
            }}
          >
            {isDeleteRolePending ? (
              <FontAwesomeIcon className="RoleConfigPage_DeleteRole_ConfirmButton_Spinner" icon="spinner" pulse />
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );

  return (
    <div className="RoleConfigPage pageStyle">
      {isDeleteRoleModalOpen && deleteRoleModal}

      {roleModalEditMode != null && (
        <RoleConfigModal
          editMode={roleModalEditMode}
          allServicePermissions={allServicePermissions}
          currRoleData={currRoleData.current}
          sendRoleConfigModalMode={(set) => {
            setRoleModalEditMode(set);
          }}
          triggerRefresh={() => {
            dispatch(
              doGetRoleList(
                (result) => {
                  setRoles(result);
                  setFilteredRoles(result);
                },
                (error) => {
                  setErrorMessage(error);
                }
              )
            );
          }}
          transmitError={(error) => {
            setErrorMessage(error);
          }}
        />
      )}

      <div className="RoleConfigPageTop">
        <div className="RoleConfigPageTop_ReturnButton">
          <ReturnButton text={'Return to Admin page'} route={'/console/admin'} />
        </div>
      </div>
      <div className="RoleConfigPage_Header">
        <div className="RoleConfigPage_HeaderTitle">
          <div className="RoleConfigPage_HeaderTitleText">Roles</div>
          {userPermissions.includes('role_add') && (
            <SvgAF
              img={IconLoader.icon_roles_add}
              hover={IconLoader.icon_roles_add_hover}
              onClick={() => {
                setRoleModalEditMode(EditRoleConfigMode.NEW);
                currRoleData.current = null;
              }}
            />
          )}
        </div>
        <div className="RoleConfigPage_HeaderSearch">
          <SearchBar
            customIcon={IconLoader.icon_search_blue}
            defaultText={'Search by Role Name'}
            searchQuery={(inputString) => setFilteredRoles(roles.filter((r) => r.role.includes(inputString)))}
            handleEnter={(event, searchBar) => {
              searchBar.props.searchQuery(searchBar.state.value);
              event.target.blur();
            }}
          />
        </div>
      </div>
      {errorMessage != null && (
        <div className="RoleConfigPage_ErrorBox">
          <div className="RoleConfigPage_ErrorBox_ContentSection">
            <div className="RoleConfigPage_ErrorBox_ContentSection_Title">Error</div>
            <div className="RoleConfigPage_ErrorBox_ContentSection_Message">{errorMessage}</div>
          </div>
          <div className="RoleConfigPage_ErrorBox_ContentSection_CloseSection">
            <SvgAF
              img={IconLoader.icon_med_close}
              hover={IconLoader.icon_med_close_hover}
              onClick={() => {
                setErrorMessage(null);
              }}
            />
          </div>
        </div>
      )}
      <div className="RoleConfigPage_Content">
        <RoleConfigTable
          data={filteredRoles}
          sendTargetRoleData={(roleId, mode) => {
            dispatch(
              doGetRoleList(
                (result) => {
                  setRoles(result);
                  //setFilteredRoles(result);
                },
                (error) => {
                  setErrorMessage(error);
                }
              )
            );
            let currRole = roles.find((r) => r._id === roleId);
            currRoleData.current = currRole;
            setRoleModalEditMode(mode);
          }}
          sendRoleDeleteInfo={(roleId, roleName) => {
            setTargetDeleteRole({ id: roleId, name: roleName });
            setIsDeleteRoleModalOpen(true);
          }}
        />
      </div>
    </div>
  );
};

export default memo(RoleConfigPage, () => true);
